import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { NotFoundScreen } from '@/features/system/NotFoundScreen'
import { PageHeader } from '@/app/layouts/PageHeader'
import { formatMoney, fromMajor } from '@/lib/money'
import { VAULTS } from './invest-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import styles from '@/features/transfers/SendAbroadScreen.module.css'

const TERMS = [1, 3, 6, 12] as const

export function OpenVaultScreen() {
  const { id } = useParams()
  const navigate = useNavigate()
  const vault = VAULTS.find((item) => item.id === id)
  const [amount, setAmount] = useState('2500')
  const [months, setMonths] = useState<number>(6)
  const [agreed, setAgreed] = useState(false)

  const model = useMemo(() => {
    const parsed = Number.parseFloat(amount.replace(/,/g, ''))
    const principal = Number.isFinite(parsed) && parsed > 0 ? parsed : 0
    const apy = vault?.apy ?? 0
    const perYear = principal * apy
    return {
      principal: fromMajor(principal, 'USD'),
      perYear: fromMajor(perYear, 'USD'),
      perMonth: fromMajor(perYear / 12, 'USD'),
      belowMinimum: principal > 0 && principal < (vault?.minimum ?? 0),
    }
  }, [amount, vault])

  if (!vault) return <NotFoundScreen />
  const needsTerm = vault.id === 'ladder'
  const ready = model.principal.minorUnits > 0 && !model.belowMinimum && agreed

  return (
    <>
      <PageHeader
        title={`Put money into ${vault.name}`}
        subtitle={`${(vault.apy * 100).toFixed(2)}% · ${vault.rateBasis} · minimum $${vault.minimum.toLocaleString('en-US')}`}
        actions={
          <Button variant="ghost" size="md" iconStart="arrow_back" to={`/invest/${vault.id}`}>
            Back to details
          </Button>
        }
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="light">
            <Stack gap={5}>
              <Stack gap={3}>
                <Text variant="caption" weight="semibold">
                  How much?
                </Text>
                <div className={styles.amountField}>
                  <Text variant="eyebrow" tone="secondary">
                    From your US Dollar account · $18,240.50 available
                  </Text>
                  <div className={styles.amountRow}>
                    <input
                      className={styles.amountInput}
                      inputMode="decimal"
                      value={amount}
                      onChange={(event) => setAmount(event.target.value.replace(/[^\d.,]/g, ''))}
                      aria-label="Amount to invest"
                    />
                    <Text variant="subtitle" weight="semibold" tone="secondary">
                      USD
                    </Text>
                  </div>
                </div>
                {model.belowMinimum && (
                  <Text variant="caption" tone="negative" role="alert">
                    The minimum for {vault.name} is ${vault.minimum.toLocaleString('en-US')}.
                  </Text>
                )}
                <Stack direction="row" gap={2} wrap>
                  {[500, 1000, 2500, 5000].map((preset) => (
                    <Button
                      key={preset}
                      variant="outline"
                      size="sm"
                      onClick={() => setAmount(String(preset))}
                    >
                      ${preset.toLocaleString('en-US')}
                    </Button>
                  ))}
                </Stack>
              </Stack>

              {needsTerm && (
                <Stack gap={3}>
                  <Text variant="caption" weight="semibold">
                    For how long?
                  </Text>
                  <Stack direction="row" gap={2} wrap>
                    {TERMS.map((term) => (
                      <Button
                        key={term}
                        variant={months === term ? 'secondary' : 'outline'}
                        size="sm"
                        onClick={() => setMonths(term)}
                      >
                        {term} {term === 1 ? 'month' : 'months'}
                      </Button>
                    ))}
                  </Stack>
                  <Text variant="eyebrow" tone="secondary">
                    Locked until the term ends. That lock is what buys the higher rate.
                  </Text>
                </Stack>
              )}

              <button
                type="button"
                onClick={() => setAgreed((value) => !value)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--pb-space-3)',
                  width: '100%',
                  textAlign: 'left',
                }}
                aria-pressed={agreed}
              >
                <Icon
                  name={agreed ? 'check_box' : 'check_box_outline_blank'}
                  size={22}
                  filled={agreed}
                />
                <Text variant="caption" weight="semibold">
                  {vault.risk < 0.3
                    ? 'I understand the rate can change and is not guaranteed.'
                    : 'I understand this is illiquid and I can lose what I put in.'}
                </Text>
              </button>
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="caption" weight="semibold" tone="onDark">
                What it would earn
              </Text>
              <Pill tone="accent">{(vault.apy * 100).toFixed(2)}%</Pill>
            </Card.Header>
            <Stack gap={1} className="pb-mt-4">
              <Text as="p" variant="display" weight="bold" tone="accent" numeric>
                {formatMoney(model.perYear)}
              </Text>
              <Text variant="caption" tone="onDarkMuted">
                a year, at today&rsquo;s rate
              </Text>
            </Stack>

            <div className={checkout.rule} />

            {[
              ['You put in', formatMoney(model.principal, { compactDecimals: true })],
              ['Per month', formatMoney(model.perMonth)],
              ['Rate basis', vault.rateBasis],
              ['Getting it back', needsTerm ? `After ${months} months` : vault.liquidity],
            ].map(([label, value]) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">
                  {label}
                </Text>
                <Text variant="caption" weight="semibold" tone="onDark" numeric>
                  {value}
                </Text>
              </div>
            ))}

            <Card.Footer>
              <Button
                size="lg"
                block
                iconEnd="arrow_forward"
                disabled={!ready}
                onClick={() => navigate('/invest/done', { replace: true })}
              >
                Move {formatMoney(model.principal, { compactDecimals: true })} in
              </Button>
              <Text variant="eyebrow" tone="onDarkMuted" className="pb-mt-3">
                {vault.id === 'vault'
                  ? 'Withdraw any day. Nothing is charged to open or close it.'
                  : 'Rates move. What you see here is today, not a promise.'}
              </Text>
            </Card.Footer>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
